<template>
  <main class="cover-auth">
    <div class="widget-auth">
      <a href="#">
        <div class="text-center mb-8">
          <img src="/public/images/logo-wide.svg" class="auth-logo" />
        </div>
      </a>

      <!-- Étape 1 : Vérification OTP -->
      <div v-if="step === 1">
        <h2 class="font-semibold text-center my-6">Vérification du code</h2>
        <p class="text-read text-grey-600 text-center pb-5">
          Saisissez le code de vérification à 6 chiffres envoyé à votre email
        </p>

        <form class="form" @submit.prevent="verifyOTP">
          <div class="flex gap-3 mb-7 justify-center">
            <input 
              v-for="(digit, index) in otpCode" 
              :key="index"
              type="text" 
              class="form-control-code" 
              :id="`otp-${index}`"
              v-model="otpCode[index]"
              maxlength="1"
              @input="handleInput(index, $event, 'otp')"
              @keydown="handleKeydown(index, $event, 'otp')"
              @paste="(e) => handlePaste(e, 'otp')"
              placeholder=""
              inputmode="numeric"
              pattern="[0-9]"
            >
          </div>
          
          <button 
            type="submit" 
            class="btn lg w-100 flex-center mt-5 mb-4"
            :disabled="!isOtpComplete || isSubmitting"
          >
            <img src="/public/icons/icon-valider.svg" class="btn-icon" alt="" />
            <span>{{ isSubmitting ? 'Vérification...' : 'Vérifier' }}</span>
          </button>

          <button 
            type="button"
            @click="resendOTP" 
            class="btn lg w-100 flex-center mb-7 btn-outline"
            :disabled="isResending"
          >
            <span>{{ isResending ? 'Envoi en cours...' : 'Renvoyer le code' }}</span>
          </button>
          
          <router-link to="/auth/sign-in">
            <h5 class="text-primary-color text-center">Voulez-vous annuler ?</h5>
          </router-link>
        </form>
      </div>

      <!-- Étape 2 : Nouveau code de sécurité -->
      <div v-if="step === 2">
        <h2 class="font-semibold text-center my-6">Nouveau code de sécurité</h2>
        <p class="text-read text-grey-600 text-center pb-5">
          Votre code de sécurité sera utilisé pour vous permettre d'accéder à votre compte.
        </p>

        <form class="form" @submit.prevent="resetPassword">
          <div class="flex gap-4 mb-7">
            <input 
              v-for="(digit, index) in securityCode" 
              :key="index"
              type="text" 
              class="form-control-code" 
              :id="`code-${index}`"
              v-model="securityCode[index]"
              maxlength="1"
              @input="handleInput(index, $event, 'code')"
              @keydown="handleKeydown(index, $event, 'code')"
              @paste="(e) => handlePaste(e, 'code')"
              placeholder=""
              inputmode="numeric"
              pattern="[0-9]"
            >
          </div>
          
          <button 
            type="submit" 
            class="btn lg w-100 flex-center mt-5 mb-7"
            :disabled="!isCodeComplete || isSubmitting"
          >
            <img src="/public/icons/icon-valider.svg" class="btn-icon" alt="" />
            <span>{{ isSubmitting ? 'Enregistrement...' : 'Valider' }}</span>
          </button>
        </form>
      </div>
    </div>
  </main>
</template>

<script lang="ts">
import { defineComponent, reactive, computed, nextTick, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import authService from '@/services/authService'
import { showAlert } from '@/utils/swal'

export default defineComponent({
  name: 'ForgetPasswordReset',
  setup() {
    const router = useRouter()
    const step = ref(1) // 1 = OTP, 2 = Nouveau code
    const otpCode = reactive<string[]>(['', '', '', '', '', ''])
    const securityCode = reactive<string[]>(['', '', '', '']) // 4 chiffres pour le code de sécurité
    const isSubmitting = ref(false)
    const isResending = ref(false)
    const identifier = ref('')

    // Vérifier qu'on vient bien de ForgetPassword
    onMounted(() => {
      const savedIdentifier = sessionStorage.getItem('resetPasswordIdentifier')
      if (!savedIdentifier) {
        router.push('/auth/forget-password')
        return
      }
      identifier.value = savedIdentifier
    })

    const isOtpComplete = computed(() => {
      return otpCode.every(digit => digit !== '' && /^\d$/.test(digit))
    })

    const isCodeComplete = computed(() => {
      return securityCode.every(digit => digit !== '' && /^\d$/.test(digit))
    })

    const focusInput = (type: string, index: number) => {
      nextTick(() => {
        const prefix = type === 'otp' ? 'otp' : 'code'
        const input = document.getElementById(`${prefix}-${index}`) as HTMLInputElement
        if (input) {
          input.focus()
        }
      })
    }

    const handleInput = (index: number, event: Event, type: string) => {
      const target = event.target as HTMLInputElement
      const value = target.value
      const array = type === 'otp' ? otpCode : securityCode
      const maxIndex = type === 'otp' ? 5 : 3

      // Ne garder que les chiffres
      if (!/^\d*$/.test(value)) {
        array[index] = ''
        return
      }

      // Si on entre un chiffre, passer au champ suivant
      if (value.length === 1 && index < maxIndex) {
        focusInput(type, index + 1)
      }
    }

    const handleKeydown = (index: number, event: KeyboardEvent, type: string) => {
      const array = type === 'otp' ? otpCode : securityCode
      const maxIndex = type === 'otp' ? 5 : 3

      // Gérer la touche Backspace
      if (event.key === 'Backspace' && array[index] === '' && index > 0) {
        event.preventDefault()
        focusInput(type, index - 1)
      }

      // Gérer les flèches gauche/droite
      if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault()
        focusInput(type, index - 1)
      }

      if (event.key === 'ArrowRight' && index < maxIndex) {
        event.preventDefault()
        focusInput(type, index + 1)
      }
    }

    const handlePaste = (event: ClipboardEvent, type: string) => {
      event.preventDefault()
      const pastedData = event.clipboardData?.getData('text')
      const array = type === 'otp' ? otpCode : securityCode
      const expectedLength = type === 'otp' ? 6 : 4
      const regex = type === 'otp' ? /^\d{6}$/ : /^\d{4}$/
      
      if (pastedData && regex.test(pastedData)) {
        for (let i = 0; i < expectedLength; i++) {
          array[i] = pastedData[i]
        }
        focusInput(type, expectedLength - 1)
      }
    }

    const verifyOTP = async () => {
      if (!isOtpComplete.value) {
        await showAlert.warning('Veuillez saisir le code à 6 chiffres')
        return
      }

      isSubmitting.value = true

      try {
        const code = otpCode.join('')
        const response = await authService.verifyOtpForReset(identifier.value, code)

        if (response.status === 0) {
          await showAlert.success('Code vérifié ! Créez maintenant votre nouveau code de sécurité')
          step.value = 2
        } else {
          await showAlert.error(response.message || 'Code incorrect')
        }
      } catch (error) {
        console.error('Erreur:', error)
        await showAlert.error('Une erreur est survenue')
      } finally {
        isSubmitting.value = false
      }
    }

    const resendOTP = async () => {
      if (isResending.value) return

      isResending.value = true

      try {
        const response = await authService.forgotPassword(identifier.value)

        if (response.status === 0) {
          await showAlert.success('Un nouveau code a été envoyé !')
          // Réinitialiser les champs
          otpCode.forEach((_, index) => otpCode[index] = '')
          focusInput('otp', 0)
        } else {
          await showAlert.error(response.message || 'Erreur lors de l\'envoi')
        }
      } catch (error) {
        console.error('Erreur:', error)
        await showAlert.error('Une erreur est survenue')
      } finally {
        isResending.value = false
      }
    }

    const resetPassword = async () => {
      if (!isCodeComplete.value) {
        await showAlert.warning('Veuillez saisir un code de sécurité à 4 chiffres')
        return
      }

      isSubmitting.value = true

      try {
        const code = securityCode.join('')
        const response = await authService.resetPasswordWithToken(code)

        if (response.status === 0) {
          sessionStorage.removeItem('resetPasswordIdentifier')
          await showAlert.success('Votre code de sécurité a été réinitialisé avec succès !')
          router.push('/auth/sign-in')
        } else {
          await showAlert.error(response.message || 'Erreur lors de la réinitialisation')
        }
      } catch (error) {
        console.error('Erreur:', error)
        await showAlert.error('Une erreur est survenue')
      } finally {
        isSubmitting.value = false
      }
    }

    return {
      step,
      otpCode,
      securityCode,
      isSubmitting,
      isResending,
      isOtpComplete,
      isCodeComplete,
      handleInput,
      handleKeydown,
      handlePaste,
      verifyOTP,
      resendOTP,
      resetPassword
    }
  }
})
</script>

<style scoped>
.auth-logo {
  max-width: 200px;
  height: auto;
}

.form-control-code {
  width: 60px;
  height: 60px;
  text-align: center;
  font-size: 24px;
  font-weight: bold;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.form-control-code:focus {
  outline: none;
  border-color: var(--primary-color, #007bff);
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.form-control-code:not(:placeholder-shown) {
  border-color: var(--primary-color, #007bff);
  background-color: #f8f9fa;
}

.flex {
  display: flex;
  justify-content: center;
}

.gap-4 {
  gap: 1rem;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-outline {
  background: transparent;
  border: 2px solid var(--primary-color, #007bff);
  color: var(--primary-color, #007bff);
}

.btn-outline:hover {
  background: var(--primary-color, #007bff);
  color: white;
}

.justify-center {
  justify-content: center;
}

.gap-3 {
  gap: 0.75rem;
}

@media (max-width: 480px) {
  .form-control-code {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
}
</style>

